import React, { Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Locale } from 'locale'
import { RouteConfig } from 'view/routes'
import { SlidingPanel } from 'view/components/panels'
import { LeftNavigationTile } from './LeftNavigationTile'
import { LeftNavigationTileLink } from './LeftNavigationTileLink'
import { navigationCollapsedGetterSelector } from 'selectors/selectionSelectors'
import { SelectionActions } from 'store/selection'
import { useRouteConfigSelectors } from './useRouteConfigSelectors'

interface LeftNavigationDefaultRouteRendererProps {
  route: RouteConfig
}

export const LeftNavigationDefaultRouteRenderer: React.FC<LeftNavigationDefaultRouteRendererProps> = ({ route }) => {
  const { numberOfItems, showActivityHighlight, showAlertHighlight, showWarnHighlight, visible } = useRouteConfigSelectors(route)
  const getNavigationCollapsed = useSelector(navigationCollapsedGetterSelector)
  const dispatch = useDispatch()

  if (!visible) {
    return null
  }

  const toggleCollapsed = (navigationEntry: string) => {
    const collapsed = getNavigationCollapsed(navigationEntry)
    dispatch(SelectionActions.setNavigationCollapsed(navigationEntry, !collapsed))
  }

  const createParentLink = (routeConfig: RouteConfig) => {
    const hasChildren = routeConfig.childRoutes

    return (
      <LeftNavigationTileLink
        url={routeConfig.url}
        label={Locale.translate(routeConfig.caption)}
        numberOfItems={numberOfItems}
        showActivityHighlight={showActivityHighlight}
        showAlertHighlight={showAlertHighlight}
        showWarnHighlight={showWarnHighlight}
        icon={routeConfig.icon}
        isLeftNavigationMinimised={false}
        isSecondary={false}
        onToggleCollapsed={hasChildren ? () => toggleCollapsed(routeConfig.url) : undefined}
        isCollapsed={getNavigationCollapsed(routeConfig.url)}
        dataTestId={`Left-Navigation-${Locale.translate(routeConfig.caption)}-Link`}
      />
    )
  }

  return (
    <>
      <LeftNavigationTile nestingLevel={0}>{createParentLink(route)}</LeftNavigationTile>
      {route.childRoutes && (
        <SlidingPanel closed={getNavigationCollapsed(route.url)} transitionOnAppear={false}>
          <LeftNavigationDefaultChildRouteRenderer childRoutes={route.childRoutes} nestingLevel={1} />
        </SlidingPanel>
      )}
    </>
  )
}

const LeftNavigationDefaultChildRouteRenderer: React.FC<{ childRoutes: RouteConfig[]; nestingLevel: number }> = ({
  childRoutes,
  nestingLevel
}) => {
  return (
    <>
      {childRoutes.map(childRoute => (
        <Fragment key={childRoute.url}>
          <ChildLink nestingLevel={nestingLevel} routeConfig={childRoute} />
          {childRoute.childRoutes && (
            <LeftNavigationDefaultChildRouteRenderer childRoutes={childRoute.childRoutes} nestingLevel={nestingLevel + 1} />
          )}
        </Fragment>
      ))}
    </>
  )
}

const ChildLink = ({ routeConfig, nestingLevel }: { routeConfig: RouteConfig; nestingLevel: number }) => {
  const { numberOfItems, showActivityHighlight, showAlertHighlight, showWarnHighlight, visible } = useRouteConfigSelectors(routeConfig)

  if (!visible) {
    return null
  }

  return (
    <LeftNavigationTile key={routeConfig.url} nestingLevel={nestingLevel}>
      <LeftNavigationTileLink
        url={routeConfig.url}
        numberOfItems={numberOfItems}
        showActivityHighlight={showActivityHighlight}
        showAlertHighlight={showAlertHighlight}
        showWarnHighlight={showWarnHighlight}
        label={Locale.translate(routeConfig.caption)}
        icon={routeConfig.icon}
        isLeftNavigationMinimised={false}
        isSecondary={true}
        dataTestId={`Left-Navigation-Nested-${Locale.translate(routeConfig.caption)}-Link`}
      />
    </LeftNavigationTile>
  )
}
